import { TrashIcon } from "@phosphor-icons/react";
import type {
  CellStyle,
  ColDef,
  ICellRendererParams,
  SortChangedEvent,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ArticleMentionText from "@/components/ui/article-card/article-mention-text";
import { Skeleton } from "@/components/ui/feedback/skeleton";
import TVEyesPlayer from "@/components/ui/TVEyesPlayer";
import useMediaFilterLabelMaps from "@/hooks/useMediaFilterLabelMaps";
import { agGridTheme } from "@/lib/ag-grid-theme";
import useDiscoveryContentManagementStore from "@/store/discovery/discovery-content-management/discovery-content-management.slice";
import useDiscoveryKeyMessageStore from "@/store/discovery/discovery-key-messages/discovery-key-message.slice";
import { useDiscoveryContext } from "@/store/discovery/discovery-params.context";

import BroadcastClipCellRenderer from "./BroadcastClipCellRenderer";
import {
  type BroadcastClipRowData,
  createBroadcastClipsUnifiedOverlay,
} from "./BroadcastClipsTableStates";
import {
  type BroadcastClipOrdering,
  createBroadcastClipsDatasource,
  startNewBroadcastClipsDatasourceSession,
} from "./broadcastClipsTableDatasource";

const DEFAULT_ORDERING: BroadcastClipOrdering = {
  airDatetimeUtc: "DESC_NULLS_LAST",
};

/** Maps AG Grid column IDs to their corresponding {@link BroadcastClipOrdering} field names. */
const COL_ID_TO_ORDERING_FIELD: Record<string, keyof BroadcastClipOrdering> = {
  AIR_DATETIME: "airDatetimeUtc",
  NATIONAL_AUDIENCE: "nationalAudience",
  NATIONAL_AD_VALUE_CENTS: "nationalAdValueCents",
  LOCAL_AUDIENCE: "localAudience",
  LOCAL_AD_VALUE_CENTS: "localAdValueCents",
  TITLE: "title",
  STATION_NAME: "stationName",
};

const CACHE_BLOCK_SIZE = 50;
const MAX_BLOCKS_IN_CACHE = 10;
const INITIAL_ROW_COUNT = 5;
const ROW_HEIGHT = 80;

const formatDateTime = (value: unknown): string => {
  const str = value as string | null;
  if (!str) return "-";
  try {
    return new Date(str).toLocaleString();
  } catch {
    return str;
  }
};

const BroadcastClipsTable = () => {
  const { getLabelMap, isLoading: isMediaFilterLabelsLoading } =
    useMediaFilterLabelMaps();
  const broadcastLanguageMap = getLabelMap("BROADCAST_LANGUAGE");

  const gridRef = useRef<AgGridReact>(null);
  const isInitialMount = useRef(true);

  const isFetchingClips = useDiscoveryContentManagementStore(
    (state) => state.isFetchingBroadcastClips,
  );
  const error = useDiscoveryContentManagementStore(
    (state) => state.broadcastClipsError,
  );
  const totalCount = useDiscoveryContentManagementStore(
    (state) => state.totalBroadcastClipsCount,
  );
  const isInitialLoading = useDiscoveryContentManagementStore(
    (state) => state.isBroadcastClipsInitialLoading,
  );
  const searchTerm = useDiscoveryContentManagementStore(
    (state) => state.broadcastClipsSearchTerm,
  );

  const setIsBroadcastExcludeModalOpen = useDiscoveryContentManagementStore(
    (state) => state.setIsBroadcastExcludeModalOpen,
  );
  const setPendingBroadcastExclusionIds = useDiscoveryContentManagementStore(
    (state) => state.setPendingBroadcastExclusionIds,
  );
  const setBroadcastExcludeSuccessCallback = useDiscoveryContentManagementStore(
    (state) => state.setBroadcastExcludeSuccessCallback,
  );
  const setSelectedBroadcastClipIds = useDiscoveryContentManagementStore(
    (state) => state.setSelectedBroadcastClipIds,
  );

  const { feedId, startDate, endDate } = useDiscoveryContext();
  const broadcastKeyMessageIds = useDiscoveryKeyMessageStore(
    (state) => state.selectedBroadcastKeyMessageIds,
  );

  const [ordering, setOrdering] =
    useState<BroadcastClipOrdering>(DEFAULT_ORDERING);

  const filters = useMemo(
    () => ({
      ...(feedId ? { feedId } : {}),
      ...(startDate && endDate
        ? { airDateStart: startDate, airDateEnd: endDate }
        : {}),
      ...(searchTerm ? { searchMatch: searchTerm } : {}),
      ...(broadcastKeyMessageIds.length > 0
        ? { keyMessageIds: broadcastKeyMessageIds }
        : {}),
    }),
    [feedId, startDate, endDate, searchTerm, broadcastKeyMessageIds],
  );

  useEffect(() => {
    startNewBroadcastClipsDatasourceSession();
  }, [filters, ordering]);

  const datasource = useMemo(() => {
    return createBroadcastClipsDatasource(
      () => filters,
      () => ordering,
    );
  }, [filters, ordering]);

  const handleRetry = useCallback(() => {
    gridRef.current?.api?.refreshInfiniteCache();
  }, []);

  // Register the grid-refresh callback so the store-connected modal can
  // trigger AG Grid side-effects without holding a reference to the grid API.
  useEffect(() => {
    setBroadcastExcludeSuccessCallback(() => {
      gridRef.current?.api?.deselectAll();
      startNewBroadcastClipsDatasourceSession();
      gridRef.current?.api?.purgeInfiniteCache();
    });
    return () => setBroadcastExcludeSuccessCallback(null);
  }, [setBroadcastExcludeSuccessCallback]);

  const handleSelectionChanged = useCallback(() => {
    if (!gridRef.current?.api) return;
    const selected =
      gridRef.current.api.getSelectedRows() as BroadcastClipRowData[];
    setSelectedBroadcastClipIds(selected.map((r) => r.ID));
  }, [setSelectedBroadcastClipIds]);

  const handleExcludeSingleRow = useCallback(
    (id: string) => {
      setPendingBroadcastExclusionIds([id]);
      setIsBroadcastExcludeModalOpen(true);
    },
    [setPendingBroadcastExclusionIds, setIsBroadcastExcludeModalOpen],
  );

  const shouldShowOverlay =
    !isFetchingClips && !error && !isInitialLoading && totalCount === 0;
  const shouldShowErrorOverlay = !isFetchingClips && !!error;

  const UnifiedOverlay = useMemo(
    () => createBroadcastClipsUnifiedOverlay(handleRetry),
    [handleRetry],
  );

  useEffect(() => {
    if (!gridRef.current?.api) return;
    if (shouldShowErrorOverlay || shouldShowOverlay) {
      gridRef.current.api.showNoRowsOverlay();
    } else {
      gridRef.current.api.hideOverlay();
    }
    if (shouldShowErrorOverlay) {
      gridRef.current.api.setRowCount(0, true);
    }
  }, [shouldShowOverlay, shouldShowErrorOverlay]);

  // Reset ordering to default when feedId changes (skip initial mount)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    if (gridRef.current?.api) {
      gridRef.current.api.purgeInfiniteCache();
    }
    setOrdering(DEFAULT_ORDERING);
  }, [feedId]);

  const handleSortChanged = (event: SortChangedEvent) => {
    if (!event.api) return;

    const columnStates = event.api.getColumnState();
    const sortedCol = columnStates.find((col) => col.sort !== null);

    if (!sortedCol?.colId) {
      setOrdering(DEFAULT_ORDERING);
      return;
    }

    const direction =
      sortedCol.sort === "asc" ? "ASC_NULLS_LAST" : "DESC_NULLS_LAST";

    const field = COL_ID_TO_ORDERING_FIELD[sortedCol.colId];
    setOrdering(field ? { [field]: direction } : DEFAULT_ORDERING);
  };

  const columnDefs: ColDef<BroadcastClipRowData>[] = [
    {
      colId: "ACTIONS",
      headerName: "",
      width: 40,
      minWidth: 40,
      maxWidth: 40,
      sortable: false,
      resizable: false,
      cellStyle: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "0 12px",
        // AG Grid's default line-height causes the icon to sit above centre;
        // 0.5 collapses it so the flex alignment takes full effect.
        lineHeight: 0.5,
      },
      cellRenderer: (params: ICellRendererParams<BroadcastClipRowData>) => {
        if (!params.data)
          return <Skeleton className="h-5 w-full align-middle mt-1.5" />;
        return (
          <button
            type="button"
            aria-label="Exclude clip"
            className="text-gray-400 hover:text-red-600 transition-colors"
            onClick={() => handleExcludeSingleRow(params.data?.ID ?? "")}
          >
            <TrashIcon size={16} aria-hidden />
          </button>
        );
      },
    },

    {
      colId: "PLAYER_URL",
      headerName: "",
      width: 48,
      minWidth: 48,
      maxWidth: 48,
      sortable: false,
      resizable: false,
      cellStyle: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "0 12px",
      },
      cellRenderer: (params: ICellRendererParams<BroadcastClipRowData>) => {
        if (!params.data)
          return <Skeleton className="h-5 w-full align-middle mt-1.5" />;
        return (
          <TVEyesPlayer
            id={params.data.ID}
            feedId={feedId ?? 0}
            type="broadcast"
            variant="play"
            meta={{
              title: params.data.TITLE,
              subtitle: params.data.STATION_NAME,
            }}
          />
        );
      },
    },

    {
      colId: "TITLE",
      field: "TITLE",
      headerName: "Title",
      width: 260,
      minWidth: 200,
      resizable: true,
      cellRenderer: (params: ICellRendererParams<BroadcastClipRowData>) => (
        <BroadcastClipCellRenderer params={params} />
      ),
    },
    {
      colId: "AIR_DATETIME",
      field: "AIR_DATETIME",
      headerName: "Air Date/Time",
      width: 180,
      minWidth: 160,
      resizable: true,
      cellRenderer: (params: ICellRendererParams<BroadcastClipRowData>) => (
        <BroadcastClipCellRenderer params={params} formatter={formatDateTime} />
      ),
    },
    {
      colId: "STATION_NAME",
      field: "STATION_NAME",
      headerName: "Station",
      width: 180,
      minWidth: 140,
      resizable: true,
      cellRenderer: (params: ICellRendererParams<BroadcastClipRowData>) => (
        <BroadcastClipCellRenderer params={params} />
      ),
    },
    {
      colId: "STATION_MEDIA_TYPE",
      field: "STATION_MEDIA_TYPE",
      headerName: "Media Type",
      width: 130,
      minWidth: 110,
      resizable: true,
      sortable: false,
      cellRenderer: (params: ICellRendererParams<BroadcastClipRowData>) => (
        <BroadcastClipCellRenderer
          params={params}
          formatter={(value: unknown) => {
            const val = value as string | null;
            if (!val) return "-";
            return val.toLowerCase() === "tv"
              ? "TV"
              : val.charAt(0).toUpperCase() + val.slice(1).toLowerCase();
          }}
        />
      ),
    },
    {
      colId: "STATION_LOCATION_DISPLAY",
      field: "STATION_LOCATION_DISPLAY",
      headerName: "Location",
      width: 200,
      minWidth: 150,
      resizable: true,
      sortable: false,
      cellRenderer: (params: ICellRendererParams<BroadcastClipRowData>) => (
        <BroadcastClipCellRenderer params={params} />
      ),
    },
    {
      colId: "NATIONAL_AUDIENCE",
      field: "NATIONAL_AUDIENCE",
      headerName: "National Audience",
      width: 170,
      minWidth: 150,
      resizable: true,
      cellRenderer: (params: ICellRendererParams<BroadcastClipRowData>) => (
        <BroadcastClipCellRenderer params={params} numberFormat="commas" />
      ),
    },
    {
      colId: "NATIONAL_AD_VALUE_CENTS",
      field: "NATIONAL_AD_VALUE_CENTS",
      headerName: "National Ad Value (¢)",
      width: 200,
      minWidth: 170,
      resizable: true,
      cellRenderer: (params: ICellRendererParams<BroadcastClipRowData>) => (
        <BroadcastClipCellRenderer params={params} numberFormat="commas" />
      ),
    },
    {
      colId: "LOCAL_AUDIENCE",
      field: "LOCAL_AUDIENCE",
      headerName: "Local Audience",
      width: 160,
      minWidth: 130,
      resizable: true,
      cellRenderer: (params: ICellRendererParams<BroadcastClipRowData>) => (
        <BroadcastClipCellRenderer params={params} numberFormat="commas" />
      ),
    },
    {
      colId: "LOCAL_AD_VALUE_CENTS",
      field: "LOCAL_AD_VALUE_CENTS",
      headerName: "Local Ad Value (¢)",
      width: 180,
      minWidth: 150,
      resizable: true,
      cellRenderer: (params: ICellRendererParams<BroadcastClipRowData>) => (
        <BroadcastClipCellRenderer params={params} numberFormat="commas" />
      ),
    },
    {
      colId: "STATION_NETWORK",
      field: "STATION_NETWORK",
      headerName: "Network",
      width: 140,
      minWidth: 110,
      resizable: true,
      sortable: false,
      cellRenderer: (params: ICellRendererParams<BroadcastClipRowData>) => (
        <BroadcastClipCellRenderer params={params} />
      ),
    },
    {
      colId: "STATION_PRIMARY_LANGUAGE",
      field: "STATION_PRIMARY_LANGUAGE",
      headerName: "Language",
      width: 130,
      minWidth: 110,
      resizable: true,
      sortable: false,
      cellRenderer: (params: ICellRendererParams<BroadcastClipRowData>) => {
        if (!params.data)
          return <Skeleton className="h-5 w-full align-middle mt-1.5" />;
        const val = params.value as string | null;
        if (!val || val === "UNKNOWN" || val === "OTHER") return "-";
        return isMediaFilterLabelsLoading && !broadcastLanguageMap.has(val) ? (
          <Skeleton className="h-5 w-full" />
        ) : (
          (broadcastLanguageMap.get(val) ?? "-")
        );
      },
    },
    {
      colId: "TRANSCRIPT_EXCERPT",
      field: "TRANSCRIPT_EXCERPT",
      headerName: "Excerpt",
      width: 400,
      minWidth: 250,
      resizable: true,
      sortable: false,
      cellStyle: {
        display: "flex",
        alignItems: "center",
        whiteSpace: "normal",
        lineHeight: "1.4",
        padding: "8px 4px",
      },
      cellRenderer: (params: ICellRendererParams<BroadcastClipRowData>) => {
        if (!params.data)
          return <Skeleton className="h-5 w-full align-middle mt-1.5" />;
        const val = params.value as string | null;
        if (!val) return "-";
        // TVEyes uses <B> tags in broadcast excerpts, <em> in other contexts.
        const termMatch = val.match(/<(?:em|B)>(.*?)<\/(?:em|B)>/i);
        const term = termMatch ? termMatch[1] : "";
        const snippet = val.replace(/<[^>]+>/g, "");
        return (
          <ArticleMentionText
            snippet={snippet}
            term={term}
            className="text-inherit"
          />
        );
      },
    },
  ];

  return (
    <div
      className="w-full h-full relative"
      aria-busy={isFetchingClips}
      aria-live="polite"
      style={{ "--ag-grid-min-height": "100px" } as React.CSSProperties}
    >
      <AgGridReact<BroadcastClipRowData>
        ref={gridRef}
        columnDefs={columnDefs}
        defaultColDef={{
          sortable: true,
          resizable: true,
          cellStyle: { display: "flex", alignItems: "center" },
        }}
        rowModelType="infinite"
        datasource={datasource}
        getRowId={(params) => params.data.ID}
        cacheBlockSize={CACHE_BLOCK_SIZE}
        maxBlocksInCache={MAX_BLOCKS_IN_CACHE}
        infiniteInitialRowCount={INITIAL_ROW_COUNT}
        rowHeight={ROW_HEIGHT}
        theme={agGridTheme}
        enableCellTextSelection
        suppressDragLeaveHidesColumns
        pagination
        paginationPageSize={50}
        paginationPageSizeSelector={[10, 20, 50, 100]}
        loadingOverlayComponent={() => null}
        noRowsOverlayComponent={UnifiedOverlay}
        onSortChanged={handleSortChanged}
        onSelectionChanged={handleSelectionChanged}
        selectionColumnDef={{
          width: 40,
          minWidth: 40,
          maxWidth: 40,
          cellStyle: {
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "0 12px",
          } as CellStyle,
        }}
        rowSelection={{
          mode: "multiRow",
          checkboxes: true,
        }}
      />
    </div>
  );
};

export default BroadcastClipsTable;
